/**
 * MarkdownModeContentComponent — orchestration surface for the markdown-mode
 * plugin's `contentComponent` slot.
 *
 * This component is mounted by the editor's `contentComponent` mechanism and
 * is rendered as a sibling to the ProseMirror `editorDOMElement` inside
 * `FullPageContentArea`. It owns:
 *
 *  1. **Snapshot** — reads the current ADF doc from the live ProseMirror view
 *     when the user transitions away from preview.
 *  2. **Dispatch tap** — installs a `dispatchTransaction` tap on the live
 *     ProseMirror view to push debounced `liveAdfDoc` updates to the source
 *     and preview views.
 *  3. **Source view rendering** — mounts `MarkdownSourceView` when
 *     `view === 'syntax'`, seeded from the snapshot and fed live updates.
 *  4. **Writeback** — pushes source-view edits back into the ProseMirror
 *     editor via `replaceWith` + `replaceDocument` meta (collab-safe).
 *  5. **Preview rendering** — delegates to a consumer-supplied
 *     `previewComponent` when `view === 'split-view'`.
 *
 * The current view (`syntax | preview | split-view`) lives in this plugin's
 * ProseMirror state (`markdownModePluginKey`); the wrapper that renders this
 * component reads it via `useSharedPluginStateSelector` and passes it down
 * as `currentView`.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { applyAnnotationRanges } from '@atlassian/mdast-util-from-pm/apply-annotation-ranges';
import type { MarkdownSourceAnnotationRange } from '@atlassian/mdast-util-from-pm/types';

import type { MarkdownModePlugin, MarkdownModePluginOptions, MarkdownModeView, } from '../markdownModePluginType';
import type { SourceViewCommand, SourceViewFormatState } from '../source-view-utils';
import { adfToMarkdown, markdownToAdf } from '../utils/markdown-conversion';

import { MarkdownSourceView } from './MarkdownSourceView';

const EMPTY_ADF_DOC: DocNode = { type: 'doc', version: 1, content: [] };

type MarkdownModeContentComponentProps = {
	api: ExtractInjectionAPI<MarkdownModePlugin> | undefined;
	config: MarkdownModePluginOptions;
	currentView: MarkdownModeView;
	editorView: EditorView;
};

export const MarkdownModeContentComponent = ({
	api,
	editorView,
	config,
	currentView,
}: MarkdownModeContentComponentProps): React.ReactElement | null => {
	if (!config.isMarkdownMode) {
		return null;
	}

	return (
		<MarkdownModeOrchestrator
			api={api}
			markdownView={currentView}
			editorView={editorView}
			config={config}
		/>
	);
};

// -------------------------------------------------------------------
// Inner orchestrator — only mounts when isMarkdownMode is true.
// -------------------------------------------------------------------

type OrchestratorProps = {
	api: ExtractInjectionAPI<MarkdownModePlugin> | undefined;
	config: MarkdownModePluginOptions;
	editorView: EditorView;
	markdownView: MarkdownModeView;
};

const MarkdownModeOrchestrator = ({ api, markdownView, editorView, config }: OrchestratorProps) => {
	// ---------------------------------------------------------------
	// State
	// ---------------------------------------------------------------
	const [adfDoc, setAdfDoc] = useState<DocNode>(EMPTY_ADF_DOC);
	const [snapshotRevision, setSnapshotRevision] = useState(0);
	const [liveAdfDoc, setLiveAdfDoc] = useState<DocNode | undefined>(undefined);
	// Snapshot markdown, pre-computed once under platform_editor_md_source_sync_snapshot.
	// `undefined` = not pre-computed; the source view converts on mount instead.
	const [seedMarkdown, setSeedMarkdown] = useState<string | undefined>(undefined);
	const liveDocDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const dispatchTapInstalledRef = useRef(false);
	const pendingWritebackNodeRef = useRef<PMNode | null>(null);
	// Annotation ranges extracted at snapshot time and updated on live doc changes.
	const [annotationRanges, setAnnotationRanges] = useState<MarkdownSourceAnnotationRange[]>([]);
	const lastAnnotationRangesRef = useRef<MarkdownSourceAnnotationRange[]>([]);
	// Current CM6-tracked annotation ranges — updated by onAnnotationRangesChange
	// after every debounced user edit in source mode. These reflect the shifted
	// positions after the user inserts or deletes text around annotated spans.
	// Used by handleSourceMarkdownChange and handleMarkdownCommit to re-apply
	// annotation marks correctly (fixes EDITOR-6844).
	const currentAnnotationRangesRef = useRef<MarkdownSourceAnnotationRange[]>([]);

	// ---------------------------------------------------------------
	// Snapshot — synchronously read the current doc from PM.
	// ---------------------------------------------------------------
	const snapshotAdf = useCallback((): DocNode | null => {
		if (!editorView || editorView.isDestroyed) {
			return null;
		}
		return editorView.state.doc.toJSON() as DocNode;
	}, [editorView]);

	// Snapshot whenever the user transitions away from preview.
	useEffect(() => {
		if (markdownView === 'preview') {
			return;
		}
		const snap = snapshotAdf();
		if (snap && snap.type === 'doc') {
			setAdfDoc(snap);
			setSnapshotRevision((r) => r + 1);
			setLiveAdfDoc(undefined);

			// Convert once here and reuse for both the source-view seed and the
			// annotation ranges (avoids a second conversion in MarkdownSourceView).
			try {
				const schema = editorView.state.schema as Schema;
				const pmDoc = editorView.state.doc;
				const { markdown, annotations } = adfToMarkdown(pmDoc, schema, {
					serializeFileMedia: config.serializeFileMedia,
				});
				if (fg('platform_editor_md_source_sync_snapshot')) {
					setSeedMarkdown(markdown);
				}
				setAnnotationRanges(annotations);
				lastAnnotationRangesRef.current = annotations;
			} catch {
				// Conversion failed — clear the seed so the source view falls back.
				if (fg('platform_editor_md_source_sync_snapshot')) {
					setSeedMarkdown(undefined);
				}
				setAnnotationRanges([]);
				lastAnnotationRangesRef.current = [];
			}
		}
	}, [markdownView, snapshotAdf, editorView, config.serializeFileMedia]);

	// ---------------------------------------------------------------
	// Dispatch tap — push debounced live updates to source/preview.
	// ---------------------------------------------------------------
	useEffect(() => {
		if (markdownView === 'preview') {
			return;
		}
		if (!editorView || editorView.isDestroyed) {
			return;
		}
		if (dispatchTapInstalledRef.current) {
			return;
		}

		const original = editorView.props.dispatchTransaction;

		const tap = (tr: Transaction) => {
			// Always run the original dispatcher first.
			if (original) {
				original.call(editorView, tr);
			} else {
				editorView.updateState(editorView.state.apply(tr));
			}

			if (!tr.docChanged) {
				return;
			}

			// Debounce: coalesce rapid keystrokes.
			if (liveDocDebounceRef.current !== null) {
				clearTimeout(liveDocDebounceRef.current);
			}
			liveDocDebounceRef.current = setTimeout(() => {
				liveDocDebounceRef.current = null;
				if (!editorView || editorView.isDestroyed) {
					return;
				}
				const json = editorView.state.doc.toJSON() as DocNode;

				// Tier 2 echo guard via Node.eq
				if (pendingWritebackNodeRef.current !== null) {
					if (pendingWritebackNodeRef.current.eq(editorView.state.doc)) {
						pendingWritebackNodeRef.current = null;
						return;
					}
					pendingWritebackNodeRef.current = null;
				}

				setLiveAdfDoc(json);
			}, 150);
		};

		editorView.setProps({ dispatchTransaction: tap });
		dispatchTapInstalledRef.current = true;

		return () => {
			// Clean up debounce timer on unmount.
			if (liveDocDebounceRef.current !== null) {
				clearTimeout(liveDocDebounceRef.current);
				liveDocDebounceRef.current = null;
			}
		};
	}, [markdownView, editorView]);

	// Clean up debounce on unmount.
	useEffect(() => {
		return () => {
			if (liveDocDebounceRef.current !== null) {
				clearTimeout(liveDocDebounceRef.current);
				liveDocDebounceRef.current = null;
			}
		};
	}, []);

	// ---------------------------------------------------------------
	// Writeback handlers
	// ---------------------------------------------------------------

	// Stable ref for serializeFileMedia so it can be read inside
	// handleSourceMarkdownChange without adding it as a useCallback dep
	// (which would recreate the callback and re-register the source listener).
	const serializeFileMediaRef = useRef(config.serializeFileMedia);
	serializeFileMediaRef.current = config.serializeFileMedia;

	/**
	 * Called when the user clicks an annotation highlight in the CodeMirror
	 * source view. Mirrors the click handler in the annotation plugin's
	 * inline-comment.ts: checks the annotation is unresolved, then dispatches
	 * setSelectedAnnotation (or setPendingSelectedAnnotation when the annotation
	 * manager is enabled) on the live ProseMirror editor so that the comment
	 * popup / comments panel opens, exactly as in the WYSIWYG view.
	 */
	const handleAnnotationClick = useCallback(
		(annotationId: string) => {
			if (!editorView || editorView.isDestroyed) {
				return;
			}
			// Read annotation plugin state via api.annotation.sharedState — avoids
			// importing internal annotation plugin utilities directly and works
			// correctly with the pre-built dist in the browser dev environment.
			const annotationPluginState = api?.annotation?.sharedState.currentState();
			if (!annotationPluginState) {
				return;
			}
			const { annotations, isAnnotationManagerEnabled } = annotationPluginState;
			// Only react to unresolved annotations (matches WYSIWYG click handler).
			const isUnresolved = annotations && annotations[annotationId] === false;
			if (!isUnresolved) {
				return;
			}
			if (isAnnotationManagerEnabled) {
				api?.annotation?.actions.setPendingSelectedAnnotation(annotationId)(
					editorView.state,
					editorView.dispatch,
				);
			} else {
				api?.annotation?.actions.setSelectedAnnotation(annotationId)(
					editorView.state,
					editorView.dispatch,
				);
			}
		},
		[editorView, api],
	);

	/**
	 * Called by MarkdownSourceView after every debounced user edit with the
	 * current CM6-tracked annotation ranges (shifted by document changes).
	 * Stores them in currentAnnotationRangesRef for use in handleSourceMarkdownChange
	 * and handleMarkdownCommit (fixes EDITOR-6844).
	 */
	const handleAnnotationRangesChange = useCallback((ranges: MarkdownSourceAnnotationRange[]) => {
		currentAnnotationRangesRef.current = ranges;
	}, []);

	/**
	 * Forwarded to MarkdownSourceView as `onRegisterCommandDispatch`.
	 * Called on mount with a stable dispatcher that routes CM6 commands to
	 * the live CodeMirror EditorView, and with `null` on unmount.
	 * Registers/unregisters the dispatcher on the markdownMode plugin action
	 * so toolbar buttons can call `api.markdownMode.actions.dispatchSourceCommand`.
	 * Only wired when `platform_editor_markdown_compatible_toolbar` is enabled.
	 */
	// Stable ref to the CM6 dispatcher — populated when MarkdownSourceView
	// mounts and cleared when it unmounts.
	const sourceDispatchRef = useRef<((cmd: SourceViewCommand) => boolean) | null>(null);

	const handleRegisterCommandDispatch = useCallback(
		(dispatcher: ((cmd: SourceViewCommand) => boolean) | null) => {
			sourceDispatchRef.current = dispatcher;
			api?.markdownMode?.actions.registerSourceCommandDispatch(dispatcher);
			if (dispatcher === null) {
				api?.markdownMode?.actions.setSourceFormatState(null);
			}
		},
		[api],
	);

	// Re-register the dispatcher whenever api changes (preset rebuild resets
	// the plugin closure, clearing sourceCommandDispatch to null).
	useEffect(() => {
		if (!fg('platform_editor_markdown_compatible_toolbar')) {
			return;
		}
		if (sourceDispatchRef.current) {
			api?.markdownMode?.actions.registerSourceCommandDispatch(sourceDispatchRef.current);
		}
	}, [api]);

	const handleSourceFormatStateChange = useCallback(
		(state: SourceViewFormatState) => {
			api?.markdownMode?.actions.setSourceFormatState(state);
		},
		[api],
	);

	/**
	 * Called by MarkdownSourceView on every debounced source change (Tier 2).
	 * Parses markdown back to ADF and dispatches a replaceWith transaction.
	 * Re-applies annotation marks using the current CM6-tracked ranges so
	 * inline comments are preserved through source-mode edits (EDITOR-6844).
	 */
	const handleSourceMarkdownChange = useCallback(
		(markdown: string) => {
			if (!editorView || editorView.isDestroyed) {
				return;
			}
			let adfNode: PMNode;
			try {
				adfNode = markdownToAdf(markdown, editorView.state.schema as Schema);
			} catch (err) {
				return;
			}

			// Re-apply annotation marks using the current CM6-tracked ranges.
			// currentAnnotationRangesRef is updated by onAnnotationRangesChange
			// (fired from MarkdownSourceView's sourceChangeListener) with positions
			// that have been mapped through the user's edits by CM6's StateField.
			// Without this, markdownToAdf produces a clean doc with no annotation
			// marks, and the replaceWith transaction below strips all inline comments
			// from the live editor (EDITOR-6844).
			if (currentAnnotationRangesRef.current.length > 0) {
				try {
					adfNode = applyAnnotationRanges(adfNode, markdown, currentAnnotationRangesRef.current);
				} catch {
					// Non-fatal: if re-application fails, proceed without annotation marks.
				}
			}
			// Skip writeback if semantically equivalent to the current doc.
			//
			// We deliberately do NOT use `adfNode.eq(editorView.state.doc)` here
			// because ProseMirror's `Node.eq` is strict on attrs — nodes created
			// by the markdown parser only carry the attrs the markdown syntax
			// can express (e.g. `url`, `alt`, `type` for media), while the live
			// editor doc carries extra default attrs assigned by the schema or
			// Confluence plugins (`width`, `height`, `__mediaTraceId`, etc.).
			// Those attrs differ even for semantically identical docs, so
			// `Node.eq` returns false and we'd fire a spurious `replaceDocument`
			// transaction that rebuilds media nodes with incomplete attrs —
			// causing them to be stripped or rendered as "Preview unavailable".
			//
			// Instead, round-trip the LIVE doc through the same markdown
			// pipeline and compare the resulting strings. If they're identical,
			// the two docs are semantically equivalent from the markdown
			// perspective and we can safely skip writeback (EDITOR-6636).
			try {
				const { markdown: liveMarkdown } = adfToMarkdown(
					editorView.state.doc,
					editorView.state.schema as Schema,
					{
						serializeFileMedia: serializeFileMediaRef.current,
					},
				);
				if (liveMarkdown === markdown) {
					return;
				}
			} catch (err) {
				// Serialisation failed (e.g. unsupported node in live doc).
				// Fall through to writeback — it's better to attempt a repair
				// than to silently skip a real change.
			}
			try {
				const { state } = editorView;
				const tr = state.tr
					.replaceWith(0, state.doc.nodeSize - 2, adfNode.content)
					.setMeta('addToHistory', false)
					.setMeta('replaceDocument', true);
				pendingWritebackNodeRef.current = tr.doc;
				editorView.dispatch(tr);
			} catch (err) {
				pendingWritebackNodeRef.current = null;
			}
		},
		[editorView],
	);

	/**
	 * Called by MarkdownSourceView on unmount / revision change when the
	 * user actually edited the buffer (Tier 0 — batch commit).
	 *
	 * Re-applies annotation marks (tracked as CM6 decoration ranges) to the
	 * parsed PM doc before dispatching — so inline comment anchors are correctly
	 * preserved after the user edits the markdown source.
	 */
	const handleMarkdownCommit = useCallback(
		(finalMarkdown: string) => {
			if (!editorView || editorView.isDestroyed) {
				return;
			}
			try {
				const liveSchema = editorView.state.schema;
				let adfNode = markdownToAdf(finalMarkdown, liveSchema);

				// Re-apply annotation marks at their (possibly shifted) character
				// positions before writing back to the editor.
				// Use currentAnnotationRangesRef (updated by onAnnotationRangesChange on
				// every debounced edit and on teardown) rather than lastAnnotationRangesRef
				// (which only holds the stale snapshot-time positions — EDITOR-6844).
				const annotationsToApply =
					currentAnnotationRangesRef.current.length > 0
						? currentAnnotationRangesRef.current
						: lastAnnotationRangesRef.current;
				if (annotationsToApply.length > 0) {
					try {
						adfNode = applyAnnotationRanges(adfNode, finalMarkdown, annotationsToApply);
					} catch {
						// Non-fatal: if re-application fails, commit without annotation marks.
					}
				}

				const { state } = editorView;
				const tr = state.tr
					.replaceWith(0, state.doc.nodeSize - 2, adfNode.content)
					.setMeta('addToHistory', false)
					.setMeta('replaceDocument', true);
				editorView.dispatch(tr);
			} catch (err) {
				// eslint-disable-next-line no-console
				console.warn(
					'[MarkdownModeContentComponent] Failed to apply markdown source edits back to the editor; discarding change.',
					err,
				);
			}
		},
		[editorView],
	);

	// ---------------------------------------------------------------
	// Hide the preview-editor ProseMirror DOM whenever the user is NOT in
	// preview view (i.e. they're in syntax or split-view).
	//
	// The platform's `FullPageContentArea` already attempts this via
	// `hideEditorContentAreaProsemirrorStyle`, but that style is gated on
	// `expValEqualsNoExposure('cc-markdown-mode', 'isEnabled', true)` AND
	// on the markdown-mode plugin's PM state being in sync — either of
	// which can fail (e.g. if the experiment value isn't loaded yet, or
	// if the contentComponent's view→PM sync hasn't run on first paint).
	// When that happens both the source CodeMirror AND the underlying
	// WYSIWYG ProseMirror render simultaneously (and any code-block
	// floating toolbar leaks through too).
	//
	// This effect targets the specific `editorView.dom` for this plugin
	// instance so it cannot affect other editors on the page, and
	// restores the original `display` value on cleanup so view→preview
	// transitions immediately re-show the editor.
	//
	// We also blur the editor when leaving preview so floating toolbars
	// (code-block toolbar, table controls, etc.) that follow editor
	// selection don't remain visible over the source view.
	useEffect(() => {
		if (!editorView || editorView.isDestroyed) {
			return;
		}
		if (markdownView === 'preview') {
			return;
		}

		const dom = editorView.dom;
		const originalDisplay = dom.style.display;
		dom.style.display = 'none';

		// Blur the editor to dismiss selection-anchored floating UI
		// (code-block toolbar, table controls). Guard against blur throwing
		// on a destroyed view.
		try {
			if (editorView.hasFocus()) {
				dom.blur();
			}
		} catch {
			// no-op
		}

		return () => {
			if (!editorView.isDestroyed) {
				dom.style.display = originalDisplay;
			}
		};
	}, [markdownView, editorView]);

	// ---------------------------------------------------------------
	// Render
	// ---------------------------------------------------------------
	const isPreview = markdownView === 'preview';

	if (isPreview) {
		return null;
	}

	const PreviewComponent = config.previewComponent;

	return (
		<>
			{/* Under sync-snapshot, wait for the first real snapshot (revision > 0) so
			    CodeMirror doesn't mount against the empty placeholder doc. */}
			{markdownView === 'syntax' &&
				(snapshotRevision > 0 || !fg('platform_editor_md_source_sync_snapshot')) && (
				<MarkdownSourceView
					contentId={config.contentId}
					initialAdfDoc={adfDoc}
					seedMarkdown={seedMarkdown}
					schema={editorView.state.schema}
					revision={snapshotRevision}
					liveAdfDoc={liveAdfDoc}
					onSourceMarkdownChange={handleSourceMarkdownChange}
					onMarkdownCommit={handleMarkdownCommit}
					serializeFileMedia={config.serializeFileMedia}
					annotationRanges={annotationRanges}
					onAnnotationRangesChange={handleAnnotationRangesChange}
					onAnnotationClick={handleAnnotationClick}
					onRegisterCommandDispatch={
						fg('platform_editor_markdown_compatible_toolbar')
							? handleRegisterCommandDispatch
							: undefined
					}
					onSourceFormatStateChange={
						fg('platform_editor_markdown_compatible_toolbar')
							? handleSourceFormatStateChange
							: undefined
					}
				/>
			)}
			{markdownView === 'split-view' && PreviewComponent && (
				<PreviewComponent key={`preview-${snapshotRevision}`} adfDoc={liveAdfDoc ?? adfDoc} />
			)}
		</>
	);
};
